import {
  digitalSourceTypeFaksimile,
  digitalSourceTypeGrafikk,
  digitalSourceTypePhotography,
  Mimir,
  ROLES,
  type Metadata,
  type Photographer,
} from "bildebanken-model";
import exifReader, { type ExpandedTags, type ExifTags, type IncludeTagsOptions } from "exifreader";
import { parseExifDateToISO, stripNtbDescription } from "./fns";
import { IPTC } from "bildebanken-model";
import { productFruitsInstance } from "services/productFruitsService";
import { get } from "svelte/store";
type extraAllowedIptcFields =
  | "OriginalTransmissionReference"
  | "TransmissionReference"
  | "DocumentNotes"
  | "ImageNotes"
  | "DigImageGUID";
type AllowedXmpTags =
  | "title"
  | "description"
  | "AltTextAccessibility"
  | "DigitalSourceType"
  | "TransmissionReference"
  | "Subject"
  | "ImageSupplierImageID"
  | "Instructions"
  | "UsageTerms"
  | "Rights"
  | "OrganisationInImageName"
  | "CopyrightOwnerName"
  | "PersonInImage";
type FieldsToImport = {
  [targetField in keyof Metadata | "DigImageGUID"]?: {
    source: keyof IncludeTagsOptions;
    field: keyof ExifTags | AllowedXmpTags | extraAllowedIptcFields;
  }[];
};
export const FIELDS_TO_IMPORT: FieldsToImport = {
  creators: [
    { source: "iptc", field: "By-line" },
    { source: "exif", field: "Artist" },
  ],
  title: [
    { source: "xmp", field: "title" },
    { source: "iptc", field: "Object Name" },
  ],
  description: [
    { source: "iptc", field: "Caption/Abstract" },
    { source: "xmp", field: "description" },
    { source: "exif", field: "ImageDescription" },
  ],
  creditLine: [
    { source: "iptc", field: "Credit" },
    { source: "xmp", field: "Credit" },
  ],
  usageTerms: [{ source: "xmp", field: "UsageTerms" }],
  digitalSourceType: [{ source: "xmp", field: "DigitalSourceType" }],
  instructions: [
    { source: "iptc", field: "Special Instructions" },
    { source: "xmp", field: "Instructions" },
    { source: "iptc", field: "DocumentNotes" },
    { source: "iptc", field: "ImageNotes" },
  ],
  altText: [{ source: "xmp", field: "AltTextAccessibility" }],
  source: [
    { source: "iptc", field: "Source" },
    { source: "xmp", field: "Source" },
  ],
  copyrightNotice: [
    { source: "xmp", field: "Rights" },
    { source: "iptc", field: "Copyright Notice" },
    { source: "exif", field: "Copyright" },
  ],
  headline: [{ source: "iptc", field: "Headline" }],
  mediaCreatedOn: [
    { source: "exif", field: "DateTimeOriginal" },
    { source: "iptc", field: "Date Created" },
  ],
  jobId: [
    { source: "iptc", field: "OriginalTransmissionReference" },
    { source: "iptc", field: "TransmissionReference" },
  ],
  supplierImageId: [{ source: "xmp", field: "ImageSupplierImageID" }],
  DigImageGUID: [{ source: "iptc", field: "DigImageGUID" }],
  location: [{ source: "iptc", field: "City" }],
  country: [{ source: "iptc", field: "Country/Primary Location Name" }],
  organisationInImageName: [{ source: "xmp", field: "OrganisationInImageName" }],
  copyrightOwners: [{ source: "xmp", field: "CopyrightOwnerName" }],
  personInImage: [{ source: "xmp", field: "PersonInImage" }],
};

export type MinimalIptcData = Partial<Record<keyof FieldsToImport, string>> & {
  thumbnail?: Buffer;
  Keywords?: string[];
};

function readIptc(file: File): Promise<MinimalIptcData> {
  if (file instanceof Blob && file.size > 0) {
    try {
      return exifReader
        .load(file, { length: "auto", expanded: true, includeOffsets: true, async: true })
        .then((embeddedData: ExpandedTags) => {
          const result = extractMetadata(embeddedData, FIELDS_TO_IMPORT) as MinimalIptcData;
          const thumbnailImage = embeddedData["Thumbnail"]?.image as Buffer;
          if (thumbnailImage) {
            result.thumbnail = thumbnailImage;
          }
          const keywords = embeddedData.iptc?.Keywords || embeddedData.xmp?.Subject;
          if (keywords) {
            result["Keywords"] = Array.isArray(keywords)
              ? keywords.map((kw) => kw.description)
              : [];
          }
          return result;
        })
        .catch((err) => {
          console.error("Error reading IPTC data from file", file.name, err);
          return {} as MinimalIptcData;
        });
    } catch (err) {
      console.error("Error reading IPTC data from file", file.name, err);
    }
  }
  return Promise.resolve({});
}

export interface SourceConfig {
  source: string;
  field: string;
}

export interface ExifTag {
  description?: string;
  value?: unknown;
  [key: string]: unknown;
}

export type MetadataConfig = Record<string, SourceConfig[]>;

export function extractMetadata(
  embeddedData: ExpandedTags,
  config: MetadataConfig,
): MinimalIptcData {
  const result: MinimalIptcData = {};

  for (const [targetField, sources] of Object.entries(config)) {
    for (const { source, field } of sources) {
      const sourceData = embeddedData?.[source];
      if (sourceData && field in sourceData) {
        const rawValue = sourceData[field];
        const value = parseExifValue(rawValue)?.trim();

        if (value) {
          result[targetField] = value;
          break;
        }
      }
    }
  }
  return result;
}
function parseExifValue(tag: unknown): string | null | undefined {
  if (tag === null || tag === undefined) return null;

  if (Array.isArray(tag)) {
    // repeatable IPTC tags come as arrays; use the first element
    return tag.length > 0 ? parseExifValue(tag[0]) : null;
  }

  if (typeof tag === "object") {
    const tagObj = tag as ExifTag;
    if (tagObj.description !== undefined) return tagObj.description;
    if (tagObj.value !== undefined) return String(tagObj.value);
  }

  return String(tag);
}

const iptcData = new Map<string, MinimalIptcData>();
function isStillsUsername(byline: string): boolean {
  return /^n\d{5,6}$/.test(byline);
}
export async function storeIptc(file: File) {
  const fileIptcData = await readIptc(file);
  const byLine = fileIptcData["creators"];
  if (byLine && isStillsUsername(byLine)) {
    delete fileIptcData["creators"];
  }
  // Sjekk om denne finnes med ny mapping
  const mimirId = fileIptcData["DigImageGUID"];
  if (mimirId) {
    get(productFruitsInstance)?.api.events.track("new-version-uploaded");
  }
  iptcData.set(file.name, fileIptcData);
}
export function linkIptcFilenameAndId(filename: string, id: string) {
  const data = iptcData.get(filename);
  if (data) {
    iptcData.set(id, data);
  } else {
    throw Error("No IPTC data available for that filename");
  }
}

export function getIptc(fileNameOrId: string): MinimalIptcData | undefined {
  return iptcData.get(fileNameOrId);
}

/**
 * Use IPTC fields to extend metadata
 *
 * If metadata contains NrkData, the function attempts to update non-destructively
 * (tweaking of this logic might be needed down the line)
 */
export function enrichMetadataFromIptc(data: MinimalIptcData, metadata: Metadata): Metadata {
  const result: Metadata = { ...metadata };

  const useNtbMapping = data.creditLine === "NTB";
  const extractedDigitalSourceType = data.digitalSourceType as IPTC.DigitalSourceType | undefined;

  if (
    data.creditLine ||
    data.copyrightNotice ||
    extractedDigitalSourceType ||
    data.instructions ||
    data.usageTerms
  ) {
    result.rights = { ...(metadata.rights || {}) };
    result.rights.note = metadata.rights?.note || data.usageTerms;
    result.rights.credit = metadata.rights?.credit || (useNtbMapping ? undefined : data.creditLine);
    result.digitalSourceType = extractedDigitalSourceType;

    if (data.instructions !== undefined) {
      result.instructions = metadata.instructions ?? data.instructions;
    }

    let rightsContact = data.copyrightNotice;
    if (rightsContact?.startsWith("NRK")) {
      rightsContact = "NRK";
    }

    if (rightsContact && !metadata.rights?.rightsHolder) {
      result.rights.rightsHolder = { contact: { title: rightsContact } };
    }
  }

  const creator = data.creators;
  if (creator) {
    const photographer: Photographer = {
      contact: { title: creator },
      role: { resId: ROLES.photographer },
    };
    result.contributors = [...(metadata.contributors || []), photographer];
  }

  if (data.instructions || data.usageTerms) {
    result.rightsMarker = "Restricted";
  }
  let description = metadata.description || data.description;
  const altText = metadata.altText || data.altText;

  if (description && useNtbMapping) {
    description = stripNtbDescription(description);
  }

  if (data.Keywords) {
    result.keywords = data.Keywords;
  }

  if (!extractedDigitalSourceType) {
    const screenshotKeywords = ["screenshot", "skjermbilde"];
    const wordInMetadataTitle = (word) => metadata.title?.toLocaleLowerCase().includes(word);
    const isScreenshot = screenshotKeywords.filter(wordInMetadataTitle).length > 0;

    if (isScreenshot) {
      result.digitalSourceType = digitalSourceTypeFaksimile;
    } else if (metadata.title?.endsWith(".png")) {
      result.digitalSourceType = digitalSourceTypeGrafikk;
    } else {
      result.digitalSourceType = digitalSourceTypePhotography;
    }
  }

  const fileExtensionReg = /\.\w+$/;
  const strippedTitle = metadata.title?.replace(fileExtensionReg, "");

  const newTitle =
    (useNtbMapping ? data.headline : undefined) || data.title || data.headline || strippedTitle;
  if (newTitle !== undefined) result.title = newTitle;
  const newHeadline = data.headline || metadata.headline;
  if (newHeadline !== undefined) result.headline = newHeadline;
  if (altText !== undefined) result.altText = altText;
  if (description !== undefined) result.description = description;
  if (data.source) result.source = data.source;
  if (data.jobId) result.jobId = data.jobId;
  if (data.supplierImageId) result.supplierImageId = data.supplierImageId;
  if (data.mediaCreatedOn) {
    const parsed = parseExifDateToISO(data.mediaCreatedOn);
    // fall back to raw value for IPTC dates already in ISO format; skip all-zero EXIF dates
    if (parsed !== undefined) {
      result.mediaCreatedOn = parsed;
    } else if (!data.mediaCreatedOn.startsWith("0000")) {
      result.mediaCreatedOn = data.mediaCreatedOn;
    }
  }
  return result;
}

export function enrichTechnicalMetadataFromIptc(
  iptc: MinimalIptcData,
  mediaType: string,
  technicalMetadata: Mimir.TechnicalMetadata | undefined,
): Mimir.TechnicalMetadata | undefined {
  const width = iptc["Image Width"]?.match(/\d+/)?.pop() || undefined;
  const widthValue = (width && parseInt(width)) || undefined;

  const height = iptc["Image Height"]?.match(/\d+/)?.pop() || undefined;
  const heightValue = (height && parseInt(height)) || undefined;

  const orientation = iptc["Orientation"] || undefined;
  const noRotation = !orientation || orientation === "top-left";

  if (widthValue && heightValue) {
    return {
      ...technicalMetadata,
      technical_image_width: noRotation ? widthValue : heightValue,
      technical_image_height: noRotation ? heightValue : widthValue,
      technical_image_file_type: mediaType,
    };
  }
  return technicalMetadata;
}
